<template>
  <v-dialog v-model="innerValue" max-width="920px" persistent>
    <v-card>
      <v-card-title class="py-3">
        {{ isEdit ? "Editar Bono" : "Crear Bono" }}
      </v-card-title>

      <v-card-text class="pt-4">
        <v-form ref="form" v-model="formValidado" lazy-validation>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.codigo" label="Código" outlined :rules="[rules.required, rules.maxLen(12)]" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.nombre" label="Nombre" outlined :rules="[rules.required, rules.maxLen(60)]" />
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete v-model="form.idsTurno" :items="turnos" item-text="nombre" item-value="idTurno"
                label="Turno (Opcional)" outlined clearable dense :loading="loadingTurno"
                :disabled="loadingTurno || loadingGuardar" multiple />
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete v-model="form.idsArea" :items="areas" item-text="nombre" item-value="idArea"
                label="Área (Opcional)" outlined clearable dense :loading="loadingArea"
                :disabled="loadingArea || loadingGuardar" multiple />
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete v-model="form.idsPuesto" :items="puestos" item-text="nombre" item-value="idPuesto"
                label="Puesto" outlined dense :rules="[rules.required]" clearable :loading="loadingDepartamento"
                :disabled="loadingDepartamento || loadingGuardar" multiple />
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete v-model="form.idsMaquina" :items="maquinas" :item-text="nombreMaquina"
                item-value="idMaquina" label="Máquina (Opcional)" outlined dense clearable :loading="loadingMaquina"
                :disabled="loadingMaquina || loadingGuardar" multiple />
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete v-model="form.idsHabilidad" :items="habilidades" item-text="nombre"
                item-value="idHabilidad" label="Habilidades (Opcional)" outlined dense clearable
                :loading="loadingHabilidades" :disabled="loadingHabilidades || loadingGuardar" multiple />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field v-model.number="form.total" label="Monto del bono" prefix="$" type="number" outlined dense
                :rules="[rules.required, rules.moneyMin(0.01)]" />
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                v-model="form.periodicidadTipo"
                :items="periodicidadTipos"
                item-text="text"
                item-value="value"
                label="Periodicidad del bono"
                outlined
                dense
                :rules="[rules.required]"
                @change="onPeriodicidadTipoChange"
              />
            </v-col>

            <v-col cols="12" md="6" v-if="form.periodicidadTipo === 'DIARIO'">
              <v-text-field
                v-model.number="form.periodicidadConfig.intervaloDias"
                label="Intervalo en días (opcional)"
                type="number"
                outlined
                dense
                :rules="[rules.intMin(1)]"
              />
            </v-col>

            <v-col cols="12" md="6" v-if="form.periodicidadTipo === 'SEMANAL'">
              <v-autocomplete
                v-model="form.periodicidadConfig.diasSemana"
                :items="diasSemanaItems"
                item-text="text"
                item-value="value"
                label="Días de la semana"
                outlined
                dense
                multiple
                chips
                small-chips
                clearable
                :rules="[rules.requiredArray('Selecciona al menos 1 día de la semana')]"
              />
            </v-col>

            <v-col cols="12" md="6" v-if="form.periodicidadTipo === 'QUINCENAL'">
              <v-select
                v-model="form.periodicidadConfig.quincenas"
                :items="quincenaItems"
                item-text="text"
                item-value="value"
                label="Cortes de quincena"
                outlined
                dense
                multiple
                chips
                small-chips
                :rules="[rules.requiredArray('Selecciona al menos 1 corte de quincena')]"
              />
            </v-col>

            <v-col cols="12" md="6" v-if="form.periodicidadTipo === 'MENSUAL'">
              <v-select
                v-model="form.periodicidadConfig.diaMes"
                :items="diaMesItems"
                item-text="text"
                item-value="value"
                label="Día de pago mensual"
                outlined
                dense
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="12" md="6" v-if="form.periodicidadTipo === 'MENSUAL'">
              <v-autocomplete
                v-model="form.periodicidadConfig.diasMes"
                :items="diaMesItems"
                item-text="text"
                item-value="value"
                label="Días mensuales adicionales (opcional)"
                outlined
                dense
                multiple
                chips
                small-chips
                clearable
              />
            </v-col>

            <v-col cols="12" md="6" v-if="form.periodicidadTipo === 'QUINCENAL'">
              <v-autocomplete
                v-model="form.periodicidadConfig.diasMes"
                :items="diaMesItems"
                item-text="text"
                item-value="value"
                label="Días de mes (opcional)"
                outlined
                dense
                multiple
                chips
                small-chips
                clearable
              />
            </v-col>

            <v-col cols="12">
              <v-alert type="info" outlined dense class="mt-2 mb-0">
                Configura el bono por periodo (diario, semanal, quincenal o mensual). Se mantiene compatibilidad con el esquema anterior.
              </v-alert>
            </v-col>

            <v-divider class="my-4" />

            <v-col cols="12" md="6">
              <v-switch
                v-model="form.usarConceptoGeneral"
                dense
                inset
                color="primary"
                label="Habilitar bono general"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-switch
                v-model="form.usarKpiBuilder"
                dense
                inset
                color="primary"
                label="Habilitar bono KPI"
              />
            </v-col>

            <v-col cols="12" v-if="!usaConceptoGeneral && !usaKpi">
              <v-alert type="warning" outlined dense class="mb-0">
                Activa al menos un tipo de bono para poder guardar.
              </v-alert>
            </v-col>

            <v-col cols="12" v-if="usaConceptoGeneral">
              <v-autocomplete v-model="form.idConceptoBono" :items="conceptosBonos" item-text="descripcion"
                item-value="idConceptoBono" label="Concepto aplicable" outlined dense :rules="[rules.required]"
                @change="onConceptChange" :loading="loadingConceptoBonos"
                :disabled="loadingConceptoBonos || loadingGuardar">
                <template v-slot:selection="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="22" class="mr-2" :color="item.color + ' lighten-5'">
                      <v-icon small :color="item.color + ' darken-2'">{{ item.icon }}</v-icon>
                    </v-avatar>
                    <span class="font-weight-medium">{{ item.descripcion }}</span>
                  </div>
                </template>

                <template v-slot:item="{ item }">
                  <div class="d-flex align-center py-2">
                    <v-avatar size="22" class="mr-2" :color="item.color + ' lighten-5'">
                      <v-icon small :color="item.color + ' darken-2'">{{ item.icon }}</v-icon>
                    </v-avatar>
                    <div>
                      <div class="font-weight-medium">{{ item.descripcion }}</div>
                      <div class="text-caption grey--text">{{ item.detalles }}</div>
                    </div>
                  </div>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col cols="12" v-if="usaKpi">
              <v-divider class="my-2" />
              <div class="d-flex align-center justify-space-between mb-2">
                <div class="text-subtitle-1 font-weight-bold">Constructor KPI Operativo</div>
                <v-chip small outlined color="primary">Periodo evaluado: {{ periodoEvaluadoLabel }}</v-chip>
              </div>
              <v-alert type="info" outlined dense class="mb-0">
                Configura reglas por indicador (ej. OEE >= 70). Esta configuración queda embebida en el bono para cálculo por periodo.
              </v-alert>
              <div class="text-caption mt-2 blue--text text--darken-2">
                El periodo evaluado se toma automáticamente de la periodicidad del bono.
              </div>
            </v-col>

            <template v-if="usaKpi">
              <v-col cols="12" md="8">
                <v-autocomplete
                  v-model="form.kpiSeleccionados"
                  :items="kpiCatalogo"
                  item-text="text"
                  item-value="value"
                  label="Indicadores para este bono"
                  outlined
                  dense
                  clearable
                  multiple
                  chips
                  small-chips
                  :rules="[rules.requiredArray('Selecciona al menos 1 indicador')]"
                  @change="onKpiSelectionChange"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  :value="form.kpiPeriodoDias"
                  label="Periodo evaluado (días)"
                  type="number"
                  outlined
                  dense
                  readonly
                />
              </v-col>

              <v-col cols="12" v-for="(rule, idx) in form.kpiRules" :key="rule.kpi + '_' + idx">
                <v-card outlined class="pa-3">
                  <v-row dense>
                    <v-col cols="12" md="3">
                      <v-text-field :value="rule.kpi" label="KPI" outlined dense readonly />
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-select
                        v-model="rule.operador"
                        :items="operadoresKpi"
                        label="Operador"
                        outlined
                        dense
                        :rules="[rules.required]"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model.number="rule.metaMinima"
                        label="Meta mínima"
                        type="number"
                        outlined
                        dense
                        :suffix="kpiSuffix(rule.kpi)"
                        :rules="[rules.required, rules.intMin(0)]"
                      />
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-text-field
                        v-model.number="rule.peso"
                        label="Peso"
                        type="number"
                        outlined
                        dense
                        suffix="%"
                        :rules="[rules.required, rules.pctRange(0, 100)]"
                      />
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-switch
                        v-model="rule.requerido"
                        inset
                        dense
                        hide-details
                        color="primary"
                        label="Obligatorio"
                      />
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>

              <v-col cols="12">
                <v-alert type="warning" outlined dense class="mb-0" v-if="!isPesoKpiValido">
                  La suma de pesos debe ser 100 % para un pago proporcional correcto.
                </v-alert>
              </v-col>
            </template>
          </v-row>
          <div v-if="usaConceptoGeneral" class="text-subtitle-1 font-weight-bold mb-2">Configuración del concepto</div>

          <!-- MÁQUINA DETENIDA (IdConceptoBono = 1) -->
          <v-row dense v-if="usaConceptoGeneral && form.idConceptoBono === 1">

            <v-col cols="12" md="6">
              <v-text-field v-model.number="form.meta" label="Tope de máquina detenida (horas)" type="number" outlined
                dense :rules="[rules.required, rules.intMin(0)]" />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field v-model.number="form.tolerancia" label="Tolerancia (horas)" type="number" outlined dense
                :rules="[rules.required, rules.intMin(0)]" />
            </v-col>

            <v-col cols="12">
              <v-alert type="info" outlined dense class="mb-0">
                Este bono evalúa el tiempo total que la máquina permanece detenida.
                <br>
                Mantenerse dentro del tope permite conservar el bono; exceder la tolerancia lo anula.
              </v-alert>
            </v-col>
          </v-row>

          <!-- TIEMPO MUERTO OPERADOR (IdConceptoBono = 2) -->
          <v-row dense v-if="usaConceptoGeneral && form.idConceptoBono === 2">

            <v-col cols="12" md="6">
              <v-text-field v-model.number="form.meta" label="Tope de tiempo muerto (horas)" type="number" outlined
                dense :rules="[rules.required, rules.intMin(0)]" />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field v-model.number="form.tolerancia" label="Tolerancia (horas)" type="number" outlined dense
                :rules="[rules.required, rules.intMin(0)]" />
            </v-col>

            <v-col cols="12">
              <v-alert type="info" outlined dense class="mb-0">
                Si el tiempo muerto se mantiene <b>por debajo del tope</b>, el operador recibe el <b>100 % del bono</b>.
                <br>
                Si el tiempo muerto <b>supera el tope</b> pero <b>no excede la tolerancia</b>, el bono se paga <b>de
                  forma
                  proporcional</b>.
                <br>
                Si el tiempo muerto <b>rebasa la tolerancia</b>, el bono <b>se anula</b>.
              </v-alert>
            </v-col>

          </v-row>

          <!-- PUNTUALIDAD (IdConceptoBono = 3) -->
          <v-row dense v-if="usaConceptoGeneral && form.idConceptoBono === 3">

            <v-col cols="12" md="6">
              <v-text-field v-model.number="form.meta" label="Días máximos de impuntualidad permitidos" type="number"
                outlined dense :rules="[rules.required, rules.intMin(0)]" />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field v-model.number="form.tolerancia" label="Tolerancia para marcar entrada (minutos)"
                type="number" outlined dense :rules="[rules.required, rules.intMin(0)]" />
            </v-col>

            <v-col cols="12">
              <v-alert type="info" outlined dense class="mb-0">
                Se evalúan las entradas registradas en el reloj checador.
                <br>
                Marcar entrada dentro de la tolerancia se considera puntual.
                <br>
                Exceder los días permitidos de impuntualidad reduce o anula el bono.
              </v-alert>
            </v-col>

          </v-row>

          <!-- ASISTENCIA (IdConceptoBono = 4) -->
          <v-row dense v-if="usaConceptoGeneral && form.idConceptoBono === 4">

            <v-col cols="12" md="6">
              <v-text-field v-model.number="form.meta" label="Faltas máximas permitidas" type="number" outlined dense
                :rules="[rules.required, rules.intMin(0)]" />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field v-model.number="form.tolerancia" label="Tolerancia de faltas" type="number" outlined dense
                :rules="[rules.required, rules.intMin(0)]" />
            </v-col>

            <v-col cols="12">
              <v-alert type="info" outlined dense class="mb-0">
                Se evalúan los días asistidos durante el periodo del bono.
                <br>
                Mantenerse dentro de las faltas permitidas conserva el bono completo.
                <br>
                Superar ese límite reduce o anula el bono.
              </v-alert>
            </v-col>

          </v-row>

          <!-- EFICIENCIA / PRODUCTIVIDAD (IdConceptoBono = 5) -->
          <v-row dense v-if="usaConceptoGeneral && form.idConceptoBono === 5">

            <v-col cols="12" md="6">
              <v-text-field v-model.number="form.meta" label="Meta de eficiencia (%)" type="number" outlined dense
                suffix="%" :rules="[rules.required, rules.intMin(0)]" />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field v-model.number="form.tolerancia" label="Mínimo de eficiencia para alcanzar el bono (%)"
                type="number" outlined dense suffix="%" :rules="[rules.required, rules.intMin(0)]" />
            </v-col>

            <v-col cols="12">
              <v-alert type="info" outlined dense class="mb-0">
                Se compara el porcentaje de eficiencia alcanzado contra la meta configurada.
                <br>
                Si la eficiencia es igual o mayor a la meta, se paga el <b>100 % del bono</b>.
                <br>
                Si la eficiencia está por debajo de la meta, pero no baja del mínimo configurado, el bono se paga <b>de
                  forma
                  proporcional</b>.
                <br>
                Si la eficiencia está por debajo del mínimo, el bono <b>no se paga</b>.
              </v-alert>
            </v-col>

          </v-row>

          <!-- EVALUACIÓN DE USO DEL SISTEMA (IdConceptoBono = 6) -->
          <v-row dense v-if="usaConceptoGeneral && form.idConceptoBono === 6">

            <v-col cols="12" md="6">
              <v-text-field v-model.number="form.meta" label="Puntaje esperado de uso del sistema" type="number"
                outlined dense :rules="[rules.required, rules.intMin(0)]" />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field v-model.number="form.tolerancia" label="Tolerancia de puntaje" type="number" outlined dense
                :rules="[rules.required, rules.intMin(0)]" />
            </v-col>

            <v-col cols="12">
              <v-alert type="info" outlined dense class="mb-0">
                Se evalúa el uso correcto y constante del sistema durante el periodo del bono.
                <br>
                Alcanzar el puntaje esperado otorga el <b>100 % del bono</b>.
                <br>
                Quedar dentro de la tolerancia reduce el bono de forma proporcional.
                <br>
                Superar el límite permitido anula el bono.
              </v-alert>
            </v-col>

          </v-row>

          <!-- NO CORRECCIÓN DE TIEMPOS (IdConceptoBono = 7) -->
          <v-row dense v-if="usaConceptoGeneral && form.idConceptoBono === 7">

            <v-col cols="12" md="6">
              <v-text-field v-model.number="form.meta" label="Máx. ediciones permitidas" type="number" outlined dense
                :rules="[rules.required, rules.intMin(0)]" />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field v-model.number="form.tolerancia" label="Tolerancia de ediciones" type="number" outlined
                dense :rules="[rules.required, rules.intMin(0)]" />
            </v-col>

            <v-col cols="12">
              <v-alert type="info" outlined dense class="mb-0">
                Se mide cuántas ediciones realiza el empleado en registros del sistema durante el periodo del bono.
                <br>
                Mantenerse dentro del máximo permitido paga el <b>100 % del bono</b>.
                <br>
                Rebasar el máximo pero permanecer dentro de la tolerancia reduce el bono <b>de forma proporcional</b>.
                <br>
                Si se rebasa la tolerancia, el bono <b>se anula</b>.
              </v-alert>
            </v-col>

          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions class="py-3 px-4">
        <v-spacer />
        <v-btn text @click="onCancel" color="success">Cancelar</v-btn>
        <v-btn text color="error" depressed :disabled="!formValidado" @click="onSave">
          Guardar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';

export default {
  props: {
    value: {
      type: Boolean,
      default: false
    },
    bono: {
      type: Object,
      default: null
    }
  },
  data() {
    return {

      conceptosBonos: [],
      turnos: [],
      areas: [],
      puestos: [],
      maquinas: [],
      habilidades: [],

      conceptosExtra: [
        {
          idConceptoBono: 1,
          icon: "mdi-engine-off",
          color: "teal",
          detalles: "Premia continuidad operativa reduciendo detenciones de máquina."
        },
        {
          idConceptoBono: 2,
          icon: "mdi-timer-off",
          color: "red",
          detalles: "Limita minutos de inactividad atribuible al operador."
        },
        {
          idConceptoBono: 3,
          icon: "mdi-clock-check-outline",
          color: "green",
          detalles: "Cuenta días de llegada a tiempo según tolerancia."
        },
        {
          idConceptoBono: 4,
          icon: "mdi-account-check",
          color: "orange",
          detalles: "Evalúa presencia en periodo y faltas permitidas."
        },
        {
          idConceptoBono: 5,
          icon: "mdi-speedometer",
          color: "blue",
          detalles: "Meta de desempeño vs esperado."
        },
        {
          idConceptoBono: 6,
          icon: "mdi-clipboard-check-outline",
          color: "teal",
          detalles: "Puntaje de uso correcto del sistema."
        },
        {
          idConceptoBono: 7,
          icon: "mdi-file-check-outline",
          color: "deep-orange",
          detalles: "Premia no editar paros/producción (o con máximo permitido)."
        }
      ],

      // Form
      form: this.buildEmptyForm(),
      diaMesItems: (function () {
        const items = [];
        for (let i = 1; i <= 31; i++) items.push({ text: String(i), value: i });
        items.push({ text: "Último día del mes", value: 0 });
        return items;
      })(),
      periodicidadTipos: [
        { text: "Diario", value: "DIARIO" },
        { text: "Semanal", value: "SEMANAL" },
        { text: "Quincenal", value: "QUINCENAL" },
        { text: "Mensual", value: "MENSUAL" }
      ],
      diasSemanaItems: [
        { text: "Lunes", value: 1 },
        { text: "Martes", value: 2 },
        { text: "Miércoles", value: 3 },
        { text: "Jueves", value: 4 },
        { text: "Viernes", value: 5 },
        { text: "Sábado", value: 6 },
        { text: "Domingo", value: 7 }
      ],
      quincenaItems: [
        { text: "1ra quincena", value: 1 },
        { text: "2da quincena", value: 2 }
      ],
      kpiCatalogo: [
        { text: "OEE", value: "OEE" },
        { text: "MTTR", value: "MTTR" },
        { text: "MTBF", value: "MTBF" },
        { text: "Eficiencia", value: "EFICIENCIA" },
        { text: "Calidad", value: "CALIDAD" },
      ],
      operadoresKpi: [">=", ">", "<=", "<", "="],

      rules: {
        required: function (v) {
          return v !== null && v !== undefined && v !== "" ? true : "Dato requerido";
        },
        maxLen: function (n) {
          return function (v) {
            if (v === null || v === undefined) return true;
            return String(v).length <= n ? true : "Máx. " + n + " caracteres";
          };
        },
        intMin: function (min) {
          return function (v) {
            if (v === null || v === undefined || v === "") return true;
            var x = Number(v);
            if (isNaN(x)) return "Dato no válido";
            return x >= min ? true : "Mínimo " + min;
          };
        },
        moneyMin: function (min) {
          return function (v) {
            if (v === null || v === undefined || v === "") return "Dato requerido";
            var x = Number(v);
            if (isNaN(x)) return "Dato no válido";
            return x >= min ? true : "Mínimo " + min;
          };
        },
        pctRange: function (min, max) {
          return function (v) {
            if (v === null || v === undefined || v === "") return true;
            var x = Number(v);
            if (isNaN(x)) return "Dato no válido";
            return x >= min && x <= max ? true : "Rango " + min + " a " + max;
          };
        },
        requiredArray: function (msg) {
          return function (v) {
            return Array.isArray(v) && v.length > 0 ? true : (msg || "Selecciona al menos 1 elemento");
          };
        }
      },

      loadingTurno: false,
      loadingArea: false,
      loadingDepartamento: false,
      loadingPuestos: false,
      loadingMaquina: false,
      loadingConceptoBonos: false,
      loadingHabilidades: false,
      loadingGuardar: false,
    };
  },
  computed: {
    innerValue: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      }
    },
    isEdit() {
      return !!this.bono;
    },
    usaKpi() {
      return !!(this.form && this.form.usarKpiBuilder);
    },
    usaConceptoGeneral() {
      return !!(this.form && this.form.usarConceptoGeneral);
    },
    periodoEvaluadoLabel() {
      const dias = Number(this.form && this.form.kpiPeriodoDias || 0);
      if (!Number.isFinite(dias) || dias <= 0) return "N/A";
      return dias + " día" + (dias === 1 ? "" : "s");
    },
    isPesoKpiValido() {
      if (!this.form || !this.usaKpi) return true;
      const rules = Array.isArray(this.form.kpiRules) ? this.form.kpiRules : [];
      if (!rules.length) return false;
      const total = rules.reduce((acc, r) => acc + Number(r.peso || 0), 0);
      return Math.abs(total - 100) < 0.0001;
    },
    formValidado() {
      const f = this.form || {}

      const reglas = [
        // Código / Nombre
        () => String(f.codigo ?? "").trim().length > 0,
        () => String(f.nombre ?? "").trim().length > 0,

        // Puesto (multiple)
        () => Array.isArray(f.idsPuesto) && f.idsPuesto.length > 0,

        // Monto
        () => Number(f.total) >= 0.01,

        // Periodicidad
        () => this.isPeriodicidadValida(f),

        // Al menos un tipo de bono
        () => !!f.usarConceptoGeneral || !!f.usarKpiBuilder,

        // Concepto general
        () => f.usarConceptoGeneral ? Number(f.idConceptoBono) > 0 : true,

        // Meta / Tolerancia para bono general (1..7)
        () => {
          if (!f.usarConceptoGeneral) return true;
          return (
            Number.isFinite(Number(f.meta)) &&
            Number(f.meta) >= 0 &&
            Number.isFinite(Number(f.tolerancia)) &&
            Number(f.tolerancia) >= 0
          )
        },

        // Máximos opcionales (si quieres respetar reglas del form)
        () => String(f.codigo ?? "").trim().length <= 12,
        () => String(f.nombre ?? "").trim().length <= 60,
        () => {
          if (!f.usarKpiBuilder) return true;
          if (!Array.isArray(f.kpiSeleccionados) || !f.kpiSeleccionados.length) return false;
          if (!Array.isArray(f.kpiRules) || !f.kpiRules.length) return false;
          const rulesOk = f.kpiRules.every(function (r) {
            return String(r.kpi || "").length > 0 &&
              [">=", ">", "<=", "<", "="].includes(String(r.operador || "")) &&
              Number.isFinite(Number(r.metaMinima)) &&
              Number(r.metaMinima) >= 0 &&
              Number.isFinite(Number(r.peso)) &&
              Number(r.peso) >= 0 &&
              Number(r.peso) <= 100;
          });
          if (!rulesOk) return false;
          return this.isPesoKpiValido;
        }
      ]

      return reglas.every(fn => fn())
    },
  },
  watch: {
    value: function (v) {
      if (v) {
        this.buildEmptyForm();
        this.initialice();
        this.hydrateForm();
      }
    },
    bono: function () {
      if (this.value) this.hydrateForm();
    },
    "form.usarKpiBuilder": function (v) {
      if (!v) return;
      if (!Array.isArray(this.form.kpiSeleccionados) || !this.form.kpiSeleccionados.length) {
        this.form.kpiSeleccionados = ["OEE"];
      }
      this.onKpiSelectionChange();
    },
    "form.periodicidadTipo": function () {
      this.syncLegacyDiasPago();
      this.syncKpiPeriodoDesdePeriodicidad();
    },
    "form.periodicidadConfig": {
      handler: function () {
        this.syncLegacyDiasPago();
        this.syncKpiPeriodoDesdePeriodicidad();
      },
      deep: true
    },
    "form.kpiRules": {
      handler: function () {
        if (this.form && this.usaKpi) this.syncMetaFromKpiRules();
      },
      deep: true
    }
  },
  methods: {
    nombreMaquina(item) {
      return `${item.marca} - [${item.modelo}]`
    },

    initialice() {
      this.getTurnos();
      this.getAreas();
      this.getDepartamentos();
      this.getPuestos();
      this.getMaquinas();
      this.getHabilidades();
      this.getConceptosBonos();
    },
    getTurnos() {
      this.loadingTurno = true;
      axios
        .get("Turnos")
        .then(response => {
          this.turnos = response.data
        })
        .catch(error => {
          console.log(error)
        })
        .finally(() => {
          this.loadingTurno = false;
        })
    },
    getAreas() {
      this.loadingArea = true;
      axios
        .get("Areas")
        .then(response => {
          this.areas = response.data
        })
        .catch(error => {
          console.log(error)
        })
        .finally(() => {
          this.loadingArea = false;
        })
    },
    getDepartamentos() {
      this.loadingDepartamento = true;
      axios
        .get("Departamento")
        .then(response => {
          this.departamentos = response.data;
        })
        .catch(error => {
          console.log(error)
        })
        .finally(() => {
          this.loadingDepartamento = false;
        })
    },
    getPuestos() {
      this.loadingPuestos = true;
      axios
        .get("Puestos")
        .then(response => {
          this.puestos = response.data;
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          this.loadingPuestos = false;
        })
    },
    getMaquinas() {
      this.loadingMaquina = true;
      axios
        .get("Maquinas")
        .then(response => {
          this.maquinas = response.data;
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          this.loadingMaquina = false;
        })
    },
    getHabilidades() {
      this.loadingHabilidades = true;
      axios
        .get("Habilidades")
        .then(response => {
          this.habilidades = response.data;
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          this.loadingHabilidades = false;
        })
    },
    getConceptosBonos() {
      this.loadingConceptoBonos = true;

      axios
        .get("/Bonos/ListarConceptoBonos")
        .then(response => {
          var lista = response.data || [];

          // Mapa: idConceptoBono -> extra
          var extraById = {};
          for (var i = 0; i < this.conceptosExtra.length; i++) {
            var ex = this.conceptosExtra[i];
            extraById[ex.idConceptoBono] = ex;
          }

          // Renombres UI por idConceptoBono
          var uiNameById = {
            5: "Eficiencia",
            6: "Evaluación de uso del sistema"
          };

          // Merge respetando el orden del API
          this.conceptosBonos = lista.map(function (c) {
            var id = c.idConceptoBono;
            var ex = extraById[id];

            return Object.assign({}, c, {
              // Para mostrar en UI (no rompe tu backend)
              NombreUI: uiNameById[id] ? uiNameById[id] : c.Descripcion,

              icon: ex ? ex.icon : "mdi-tag",
              color: ex ? ex.color : "grey",
              detalles: ex ? ex.detalles : ""
            });
          });
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          this.loadingConceptoBonos = false;
        });
    },

    postBono() {
      this.loadingGuardar = true;
      const payload = this.buildPayload();
      axios
        .post("Bonos/Crear", payload)
        .then(() => {
          this.$emit("reload");
          this.innerValue = false;
        })
        .catch(error => console.log(error))
        .finally(() => this.loadingGuardar = false)
    },
    putBono() {
      this.loadingGuardar = true;
      const payload = this.buildPayload();
      axios
        .put(`Bonos/Actualizar/${this.form.idBono}`, payload)
        .then(() => {
          this.innerValue = false;
          this.$emit("reload");
        })
        .catch(error => console.log(error))
        .finally(() => this.loadingGuardar = false)
    },

    buildEmptyForm() {
      return {
        idBono: null,
        codigo: "",
        nombre: "",
        idsTurno: [],
        idsArea: [],
        idsPuesto: [],
        idsMaquina: [],
        idsHabilidad: [],
        total: 0,
        periodicidadTipo: "MENSUAL",
        periodicidadConfig: {
          intervaloDias: 1,
          diasSemana: [],
          quincenas: [],
          diaMes: 0,
          diasMes: []
        },
        diasPago: [],
        idConceptoBono: null,
        meta: null,
        tolerancia: null,
        usarKpiBuilder: false,
        usarConceptoGeneral: true,
        kpiSeleccionados: [],
        kpiRules: [],
        kpiPeriodoDias: 30,
      };
    },

    hydrateForm() {
      // reset
      this.form = this.buildEmptyForm();

      // edición
      if (this.bono) {
        // copia simple (evitando optional chaining)
        this.form.idBono = this.bono.idBono || null;
        this.form.codigo = this.bono.codigo || "";
        this.form.nombre = this.bono.nombre || "";
        this.form.idsTurno = this.bono.idsTurno || [];
        this.form.idsArea = this.bono.idsArea || [];
        this.form.idsPuesto = this.bono.idsPuesto || [];
        this.form.idsMaquina = this.bono.idsMaquina || [];
        this.form.idsHabilidad = this.bono.idsHabilidad || [];
        this.form.total = this.bono.total || 0;
        this.form.periodicidadTipo = this.normalizePeriodicidadTipo(this.bono.periodicidadTipo);
        this.form.periodicidadConfig = this.normalizePeriodicidadConfig(this.bono.periodicidadConfig);
        this.form.diasPago = Array.isArray(this.bono.diasPago) ? this.bono.diasPago : [];
        this.form.idConceptoBono = this.bono.idConceptoBono || null;
        this.form.meta = this.bono.meta || null;
        this.form.tolerancia = this.bono.tolerancia || null;
        const tieneKpi = this.hydrateKpiConfig(this.bono);
        const tieneGeneral = Number(this.form.idConceptoBono) > 0;
        this.form.usarConceptoGeneral = tieneGeneral || !tieneKpi;
        this.form.usarKpiBuilder = tieneKpi;

        if (!this.bono.periodicidadTipo) {
          this.hydrateLegacyPeriodicidadFromDiasPago(this.form.diasPago);
        }
      }

      this.syncLegacyDiasPago();
      this.syncKpiPeriodoDesdePeriodicidad();

      this.applyConceptDefaults(this.form.idConceptoBono);
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
    },
    onConceptChange(conceptId) {
      this.applyConceptDefaults(conceptId);
    },
    onKpiSelectionChange() {
      const selected = Array.isArray(this.form.kpiSeleccionados) ? this.form.kpiSeleccionados.slice() : [];
      const current = Array.isArray(this.form.kpiRules) ? this.form.kpiRules.slice() : [];
      const byKpi = current.reduce(function (acc, r) {
        acc[String(r.kpi)] = r;
        return acc;
      }, {});

      const nextRules = selected.map((kpi) => {
        const ex = byKpi[String(kpi)];
        if (ex) return ex;
        return this.buildDefaultKpiRule(kpi);
      });

      const totalPeso = nextRules.reduce((acc, r) => acc + Number(r.peso || 0), 0);
      if (nextRules.length > 0 && totalPeso === 0) {
        const base = Math.floor(100 / nextRules.length);
        let rem = 100 - (base * nextRules.length);
        nextRules.forEach((r) => {
          r.peso = base + (rem > 0 ? 1 : 0);
          if (rem > 0) rem -= 1;
        });
      }

      this.form.kpiRules = nextRules;

      if (this.usaKpi) this.syncMetaFromKpiRules();
    },
    buildDefaultKpiRule(kpi) {
      const key = String(kpi || "").toUpperCase();
      let metaMinima = 0;
      if (key === "OEE") metaMinima = 70;
      if (key === "EFICIENCIA") metaMinima = 80;
      if (key === "CALIDAD") metaMinima = 98;
      if (key === "MTTR") metaMinima = 30;
      if (key === "MTBF") metaMinima = 120;
      return {
        kpi: key,
        operador: key === "MTTR" ? "<=" : ">=",
        metaMinima: metaMinima,
        peso: 0,
        requerido: key === "OEE"
      };
    },
    kpiSuffix(kpi) {
      const key = String(kpi || "").toUpperCase();
      if (key === "OEE" || key === "EFICIENCIA" || key === "CALIDAD") return "%";
      if (key === "MTTR") return "min";
      if (key === "MTBF") return "h";
      return "";
    },
    syncMetaFromKpiRules() {
      // Si el bono también usa concepto general, no sobrescribir meta/tolerancia.
      return;
    },
    getPeriodoEvaluadoDiasFromPeriodicidad() {
      const tipo = this.normalizePeriodicidadTipo(this.form.periodicidadTipo);
      const cfg = this.normalizePeriodicidadConfig(this.form.periodicidadConfig);
      if (tipo === "DIARIO") return Number(cfg.intervaloDias || 1);
      if (tipo === "SEMANAL") return 7;
      if (tipo === "QUINCENAL") return 15;
      return 30;
    },
    syncKpiPeriodoDesdePeriodicidad() {
      this.form.kpiPeriodoDias = this.getPeriodoEvaluadoDiasFromPeriodicidad();
    },
    hydrateKpiConfig(bono) {
      if (!bono || typeof bono !== "object") return;
      let raw = bono.kpiConfig || bono.configuracionKpi || bono.configKpi || bono.kpiRulesJson || null;
      if (typeof raw === "string") {
        try {
          raw = JSON.parse(raw);
        } catch (e) {
          raw = null;
        }
      }
      if (!raw || typeof raw !== "object") return;

      const selected = Array.isArray(raw.kpis) ? raw.kpis.map(x => String(x || "").toUpperCase()).filter(Boolean) : [];
      const rules = Array.isArray(raw.rules) ? raw.rules : [];

      this.form.usarKpiBuilder = selected.length > 0 || rules.length > 0;
      this.form.kpiSeleccionados = selected;
      this.form.kpiPeriodoDias = Number(raw.periodoDias) > 0 ? Number(raw.periodoDias) : this.getPeriodoEvaluadoDiasFromPeriodicidad();
      this.form.kpiRules = rules.map((r) => ({
        kpi: String(r.kpi || "").toUpperCase(),
        operador: [">=", ">", "<=", "<", "="].includes(String(r.operador || "")) ? String(r.operador) : ">=",
        metaMinima: Number(r.metaMinima || 0),
        peso: Number(r.peso || 0),
        requerido: !!r.requerido
      })).filter(r => r.kpi);

      if (!this.form.kpiRules.length && selected.length) {
        this.form.kpiRules = selected.map(this.buildDefaultKpiRule);
      }
      this.syncKpiPeriodoDesdePeriodicidad();
      return this.form.usarKpiBuilder;
    },
    onPeriodicidadTipoChange(tipo) {
      this.form.periodicidadTipo = this.normalizePeriodicidadTipo(tipo);
      const intervaloDias = Number(this.form.periodicidadConfig.intervaloDias);
      this.$set(this.form.periodicidadConfig, "intervaloDias", Number.isFinite(intervaloDias) && intervaloDias >= 1 ? intervaloDias : 1);
      if (this.form.periodicidadTipo === "SEMANAL" && !Array.isArray(this.form.periodicidadConfig.diasSemana)) {
        this.$set(this.form.periodicidadConfig, "diasSemana", []);
      }
      if (this.form.periodicidadTipo === "QUINCENAL" && !Array.isArray(this.form.periodicidadConfig.quincenas)) {
        this.$set(this.form.periodicidadConfig, "quincenas", []);
      }
      if (!Array.isArray(this.form.periodicidadConfig.diasMes)) {
        this.$set(this.form.periodicidadConfig, "diasMes", []);
      }
      if (this.form.periodicidadTipo === "MENSUAL") {
        const diaMes = Number(this.form.periodicidadConfig.diaMes);
        this.$set(this.form.periodicidadConfig, "diaMes", Number.isFinite(diaMes) ? diaMes : 0);
      }
      this.syncLegacyDiasPago();
    },
    normalizePeriodicidadTipo(tipo) {
      const t = String(tipo || "").trim().toUpperCase();
      if (t === "DIARIO" || t === "SEMANAL" || t === "QUINCENAL" || t === "MENSUAL") return t;
      return "MENSUAL";
    },
    normalizePeriodicidadConfig(config) {
      let raw = config;
      if (typeof raw === "string") {
        try {
          raw = JSON.parse(raw);
        } catch (e) {
          raw = {};
        }
      }
      if (!raw || typeof raw !== "object") raw = {};

      const intervaloDias = Number(raw.intervaloDias);
      const diasSemana = Array.isArray(raw.diasSemana) ? raw.diasSemana.map(Number).filter(n => Number.isFinite(n)) : [];
      const quincenas = Array.isArray(raw.quincenas) ? raw.quincenas.map(Number).filter(n => n === 1 || n === 2) : [];
      const diaMes = Number(raw.diaMes);
      const diasMes = Array.isArray(raw.diasMes) ? raw.diasMes.map(Number).filter(n => Number.isFinite(n) && n >= 0 && n <= 31) : [];

      return {
        intervaloDias: Number.isFinite(intervaloDias) && intervaloDias >= 1 ? intervaloDias : 1,
        diasSemana: diasSemana,
        quincenas: quincenas,
        diaMes: Number.isFinite(diaMes) ? diaMes : 0,
        diasMes: diasMes
      };
    },
    hydrateLegacyPeriodicidadFromDiasPago(diasPago) {
      const dias = Array.isArray(diasPago) ? diasPago.map(Number).filter(n => Number.isFinite(n)) : [];
      this.form.periodicidadTipo = "MENSUAL";
      this.form.periodicidadConfig = {
        intervaloDias: 1,
        diasSemana: [],
        quincenas: [],
        diaMes: dias.length ? dias[0] : 0,
        diasMes: dias.length ? dias.slice() : []
      };
    },
    isPeriodicidadValida(f) {
      const tipo = this.normalizePeriodicidadTipo(f.periodicidadTipo);
      const cfg = this.normalizePeriodicidadConfig(f.periodicidadConfig);

      if (tipo === "DIARIO") return Number(cfg.intervaloDias) >= 1;
      if (tipo === "SEMANAL") return Array.isArray(cfg.diasSemana) && cfg.diasSemana.length > 0;
      if (tipo === "QUINCENAL") return (Array.isArray(cfg.quincenas) && cfg.quincenas.length > 0) || (Array.isArray(cfg.diasMes) && cfg.diasMes.length > 0);
      if (tipo === "MENSUAL") return Number.isFinite(Number(cfg.diaMes)) || (Array.isArray(cfg.diasMes) && cfg.diasMes.length > 0);
      return false;
    },
    syncLegacyDiasPago() {
      const tipo = this.normalizePeriodicidadTipo(this.form.periodicidadTipo);
      const cfg = this.normalizePeriodicidadConfig(this.form.periodicidadConfig);

      if (tipo === "MENSUAL") {
        if (Array.isArray(cfg.diasMes) && cfg.diasMes.length > 0) {
          this.form.diasPago = cfg.diasMes.slice();
        } else {
          this.form.diasPago = [Number(cfg.diaMes)];
        }
        return;
      }

      if (tipo === "QUINCENAL") {
        if (Array.isArray(cfg.diasMes) && cfg.diasMes.length > 0) {
          this.form.diasPago = cfg.diasMes.slice();
          return;
        }
        if (Array.isArray(cfg.quincenas) && cfg.quincenas.length > 0) {
          const mapped = [];
          if (cfg.quincenas.includes(1)) mapped.push(15);
          if (cfg.quincenas.includes(2)) mapped.push(0);
          this.form.diasPago = mapped.length ? mapped : [15, 0];
          return;
        }
        this.form.diasPago = [15, 0];
        return;
      }

      // Compatibilidad legacy: para no mensuales enviar último día del mes.
      this.form.diasPago = [0];
    },
    buildPayload() {
      const payload = Object.assign({}, this.form);
      payload.periodicidadTipo = this.normalizePeriodicidadTipo(payload.periodicidadTipo);
      payload.periodicidadConfig = this.normalizePeriodicidadConfig(payload.periodicidadConfig);

      // Enviar fallback legacy para registros/servicios antiguos.
      this.syncLegacyDiasPago();
      payload.diasPago = Array.isArray(this.form.diasPago) ? this.form.diasPago.slice() : [0];

      const kpiConfig = this.usaKpi ? {
        version: 1,
        tipo: "KPI_OPERATIVO",
        periodoDias: Number(this.getPeriodoEvaluadoDiasFromPeriodicidad()),
        kpis: Array.isArray(this.form.kpiSeleccionados) ? this.form.kpiSeleccionados.map(x => String(x || "").toUpperCase()) : [],
        rules: Array.isArray(this.form.kpiRules) ? this.form.kpiRules.map((r) => ({
          kpi: String(r.kpi || "").toUpperCase(),
          operador: String(r.operador || ">="),
          metaMinima: Number(r.metaMinima || 0),
          peso: Number(r.peso || 0),
          requerido: !!r.requerido
        })) : []
      } : null;
      payload.kpiConfig = kpiConfig;
      payload.configuracionKpi = kpiConfig ? JSON.stringify(kpiConfig) : null;
      payload.usarKpiBuilder = !!this.usaKpi;

      if (!this.usaConceptoGeneral) {
        payload.idConceptoBono = null;
        payload.meta = null;
        payload.tolerancia = null;
      }

      if (!this.usaKpi) {
        payload.kpiConfig = null;
        payload.configuracionKpi = null;
        payload.kpiSeleccionados = [];
        payload.kpiRules = [];
      }

      return payload;
    },
    applyConceptDefaults(conceptId) {
      const id = Number(conceptId);
      if (!Number.isFinite(id) || id <= 0) return;

      if (this.form.meta === null || this.form.meta === undefined) {
        this.form.meta = id === 5 ? 85 : 0;
      }
      if (this.form.tolerancia === null || this.form.tolerancia === undefined) {
        this.form.tolerancia = 0;
      }
    },
    onCancel() {
      this.innerValue = false;
      this.$emit("cancel");
    },
    onSave() {
      if (!this.$refs.form) return;
      var ok = this.$refs.form.validate();
      if (!ok) return;
      if (this.isEdit) {
        this.putBono();
      } else {
        this.postBono();
      }
    }
  }
};
</script>

<style scoped>
.bono-select {
  min-width: 320px;
}
</style>
